<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/main/PosMainOrderCard.vue
  // (lucide→mdi icons, money via usePosMoney, no dark: variants).
  import { computed, getCurrentInstance, ref, useListeners } from 'vue';
  import { onLongPress } from '@vueuse/core';
  import { OrderStatus, OrderType } from '@/enums/OrderEnums';
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import {
    formatOrderItemsPreview,
    getOrderCardBodyCustomerLine,
    getOrderCardHeaderText,
    getPaymentStatusInfo,
  } from '@/domain/pos/order-formatting';
  import { getPosOrderTypeIcon } from '@/domain/pos/order-type-ui';
  import { usePosMoney } from '@/composition/use-pos-money';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = withDefaults(
    defineProps<{
      order: IOrderListItem;
      /**
       * History lists span up to 90 days, so HH:MM alone is ambiguous there:
       * they show "dd/MM, HH:mm" (same format as the session cards). Live
       * lists (today only) keep the compact time.
       */
      showDate?: boolean;
    }>(),
    { showDate: false }
  );

  const emit = defineEmits<{
    (e: 'click', order: IOrderListItem): void;
    /** Secondary action: touch long-press or right-click (read-only preview). */
    (e: 'longpress', order: IOrderListItem): void;
  }>();

  const money = usePosMoney();
  const proxy = getCurrentInstance()!.proxy;
  const listeners = useListeners();

  function formatTime(timestamp: number): string {
    if (props.showDate) {
      return new Date(timestamp).toLocaleString(proxy.$i18n.locale, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(timestamp).toLocaleTimeString(proxy.$i18n.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusLabelKey(status: string): string | null {
    switch (status) {
      case OrderStatus.Received:
        return 'posV2.statusReceived';
      case OrderStatus.Progress:
        return 'posV2.statusProgress';
      case OrderStatus.ReadyToTake:
        return 'posV2.statusReady';
      case OrderStatus.Delivering:
        return 'posV2.statusDelivering';
      case OrderStatus.Finished:
        return 'posV2.statusFinished';
      case OrderStatus.Canceled:
        return 'posV2.statusCanceled';
      case OrderStatus.Declined:
        return 'posV2.statusDeclined';
      default:
        return null;
    }
  }

  function getStatusColor(
    status: string
  ): 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'info' {
    switch (status) {
      case OrderStatus.Received:
        return 'primary';
      case OrderStatus.Progress:
        return 'warning';
      case OrderStatus.ReadyToTake:
        return 'success';
      case OrderStatus.Delivering:
        return 'info';
      case OrderStatus.Finished:
        return 'neutral';
      case OrderStatus.Canceled:
      case OrderStatus.Declined:
        return 'error';
      default:
        return 'neutral';
    }
  }

  const headerText = computed(() => getOrderCardHeaderText(props.order));

  const customerLine = computed(() =>
    getOrderCardBodyCustomerLine(props.order, headerText.value)
  );

  const headerLeadingIcon = computed(() => {
    // Table orders: when header is the customer name, prepend a user icon.
    if (
      props.order.type === OrderType.Seat &&
      String(props.order.customerName ?? '').trim() !== ''
    ) {
      return 'account';
    }
    return getPosOrderTypeIcon(props.order.type as OrderType);
  });

  const itemsPreview = computed(() =>
    formatOrderItemsPreview(props.order.items, 2)
  );

  const paymentStatusInfo = computed(() =>
    getPaymentStatusInfo(props.order.paymentStatus)
  );

  const discountBadgeText = computed(() => {
    const badge = props.order.discountBadge;
    if (!badge) return null;
    return badge.kind === 'percent'
      ? `-${badge.value}%`
      : `-${money.formatComputedMoney(badge.value)}`;
  });

  const statusClass = computed(() => {
    if (props.order.needsAttention) return 'status-attention';

    switch (props.order.status) {
      case OrderStatus.Progress:
        return 'status-progress';
      case OrderStatus.ReadyToTake:
        return 'status-ready';
      case OrderStatus.Delivering:
        return 'status-delivering';
      default:
        return 'status-default';
    }
  });

  //#region Long-press / right-click secondary action (same pattern as
  // PosFloorTableTile.vue)

  const rootRef = ref<HTMLElement>();

  // One-shot per press: dedupes onLongPress + contextmenu (Android fires the
  // latter on touch long-press, Windows on right-button release) and swallows
  // the click that follows a long-press release, no matter how long the hold
  // lasted. Reset on the next press so a stale flag can never eat a click.
  let longPressFired = false;

  function handlePointerDown() {
    longPressFired = false;
  }

  function triggerLongPress() {
    if (longPressFired) return;
    longPressFired = true;
    emit('longpress', props.order);
  }

  // Only arm the machinery when a consumer listens: elsewhere a slow press
  // must keep behaving as a plain click.
  if (listeners.longpress) {
    onLongPress(rootRef, triggerLongPress, { delay: 500 });
  }

  function handleContextMenu(event: MouseEvent) {
    // Only hijack the context menu when a consumer listens.
    if (!listeners.longpress) return;
    event.preventDefault();
    triggerLongPress();
  }

  //#endregion

  function handleClick() {
    if (longPressFired) {
      longPressFired = false;
      return;
    }
    emit('click', props.order);
  }
</script>

<template>
  <button
    ref="rootRef"
    type="button"
    class="pos-v2-order-card"
    :class="statusClass"
    @pointerdown="handlePointerDown"
    @click="handleClick"
    @contextmenu="handleContextMenu"
  >
    <div class="card-header">
      <div class="header-left">
        <PosUiIcon :name="headerLeadingIcon" class="type-icon" />
        <span class="order-title">{{ headerText }}</span>
      </div>
      <div class="header-right">
        <PosUiBadge
          :color="getStatusColor(order.status)"
          variant="soft"
          size="xs"
        >
          {{
            getStatusLabelKey(order.status)
              ? $t(getStatusLabelKey(order.status))
              : order.status
          }}
        </PosUiBadge>
        <span class="order-time">{{ formatTime(order.updatedAt) }}</span>
      </div>
    </div>

    <div class="card-body">
      <div v-if="customerLine" class="info-row customer">
        <PosUiIcon :name="customerLine.icon" class="info-icon" />
        <span class="info-text">{{ customerLine.text }}</span>
      </div>

      <div v-if="itemsPreview" class="info-row items">
        <PosUiIcon name="cart" class="info-icon" />
        <span class="info-text">{{ itemsPreview }}</span>
      </div>

      <div v-if="order.comment" class="info-row comment">
        <PosUiIcon name="comment-text-outline" class="info-icon" />
        <span class="info-text">{{ order.comment }}</span>
      </div>
    </div>

    <div class="card-footer">
      <div class="footer-left">
        <PosUiBadge
          :color="paymentStatusInfo.color"
          variant="soft"
          size="xs"
          icon="wallet"
          class="payment-badge"
        >
          {{ $t(paymentStatusInfo.i18nKey) }}
        </PosUiBadge>
        <!-- Sky/info: informational role (emerald would read as "paid"). -->
        <PosUiBadge
          v-if="discountBadgeText"
          color="info"
          variant="soft"
          size="xs"
          icon="tag-outline"
          class="discount-badge"
        >
          {{ discountBadgeText }}
        </PosUiBadge>
        <span class="total-value">
          {{ money.formatComputedMoney(order.total) }}
        </span>
      </div>
    </div>
  </button>
</template>

<style scoped lang="scss">
  @keyframes attention-border-pulse {
    0%,
    100% {
      border-left-color: rgb(248 113 113);
    }
    50% {
      border-left-color: rgb(220 38 38);
    }
  }

  // White card + 4px left status accent (Shopify order-list pattern):
  // status reads at a glance without painting the whole card.
  .pos-v2-order-card {
    @apply tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-l-4 tw-border-neutral-200 tw-bg-white tw-p-3 tw-text-left tw-transition-colors tw-duration-150;

    &:hover {
      @apply tw-border-neutral-300 tw-bg-neutral-50;
    }

    &.status-default {
      @apply tw-border-l-neutral-300;
    }

    &.status-progress {
      @apply tw-border-l-amber-400;

      &:hover {
        @apply tw-border-l-amber-400;
      }
    }

    &.status-ready {
      @apply tw-border-l-emerald-500;

      &:hover {
        @apply tw-border-l-emerald-500;
      }
    }

    &.status-delivering {
      @apply tw-border-l-sky-400;

      &:hover {
        @apply tw-border-l-sky-400;
      }
    }

    &.status-attention {
      @apply tw-border-l-red-400 tw-bg-red-50/60;
      animation: attention-border-pulse 2s ease-in-out infinite;

      &:hover {
        @apply tw-bg-red-50;
      }
    }

    .card-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-2;

      .header-left {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1.5;

        .type-icon {
          @apply tw-flex-none tw-text-base tw-text-neutral-400;
        }

        .order-title {
          @apply tw-min-w-0 tw-truncate tw-text-sm tw-font-bold tw-text-neutral-900;
        }
      }

      .header-right {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-2;

        .order-time {
          @apply tw-text-xs tw-font-medium tw-text-neutral-500;
        }
      }
    }

    .card-body {
      @apply tw-flex tw-flex-col tw-gap-1;

      .info-row {
        @apply tw-flex tw-min-w-0 tw-items-start tw-gap-1.5;

        .info-icon {
          @apply tw-mt-0.5 tw-flex-shrink-0 tw-text-sm tw-text-neutral-400;
        }

        .info-text {
          @apply tw-truncate tw-text-xs tw-text-neutral-600;
        }

        &.customer .info-text {
          @apply tw-font-medium tw-text-neutral-700;
        }

        &.items .info-text {
          @apply tw-line-clamp-2 tw-whitespace-normal;
        }

        &.comment .info-text {
          @apply tw-italic tw-text-neutral-500;
        }
      }
    }

    .card-footer {
      @apply tw-flex tw-items-center tw-justify-between tw-border-t tw-border-neutral-100 tw-pt-2;

      .footer-left {
        @apply tw-flex tw-items-center tw-gap-2;

        .total-value {
          @apply tw-text-sm tw-font-bold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }
  }
</style>
